import * as R from 'ramda'
import { useRef, useState } from 'react'
import { List, ListItemButton, Typography } from '@mui/material'
import { HallPermissions } from '@weblite/ts-action/hall'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { SettingsSection } from '$common/settings-section'
import { RoomPermissions, RoomPermissionsRef } from '$common/room-permissions'
import { UserListItemWithStatus } from '$common/user-list-item'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { nothing } from '$func/utils'
import { AppBarBackTitle } from '$common/app-bar/app-bar-back-title'

interface Props<Right, Permissions> {
  pageTitle: string
  subTitle: string
  userId: string
  rights: Right[]
  confirmDisabled?: boolean
  initialPermissions: Permissions
  actionButtonProps?: {
    color: string
    text: string
  }
  secondaryActionButtonProps?: {
    color: string
    text: string
  }
  onBack: () => unknown
  onComplete: (permissions: Permissions) => Promise<unknown>
  onActionButtonClick: () => unknown
  onSecondaryActionButton?: () => unknown
}

export function HallPermissionsPage<
  Right extends {
    id: keyof HallPermissions
    isLocked: boolean
    localId: string
    requires?: string
    exclusive?: boolean
  },
  Permissions extends Partial<HallPermissions>,
>({
  userId,
  pageTitle,
  subTitle,
  rights,
  confirmDisabled = false,
  actionButtonProps = {
    text: '',
    color: '',
  },
  secondaryActionButtonProps = {
    text: '',
    color: '',
  },
  initialPermissions,
  onBack,
  onComplete,
  onActionButtonClick,
  onSecondaryActionButton = nothing,
}: Props<Right, Permissions>) {
  const permissionsRef = useRef<RoomPermissionsRef<Permissions>>(null)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isAllChecked, setIsAllChecked] = useState(false) // TODO: if this check is not needed remove this

  const onConfirmHandler = async () => {
    const { current: roomPermissions } = permissionsRef
    if (!roomPermissions) return

    const values = roomPermissions.getValues()
    if (values) await onComplete(values)
  }

  const hasConfirmationButton = !R.all(R.prop('isLocked'), rights)

  return (
    <PageWithAppBar
      appBar={
        hasConfirmationButton ? (
          <AppBarBackTitleConfirm
            title={pageTitle}
            onBack={onBack}
            onConfirm={onConfirmHandler}
            confirmDisabled={confirmDisabled}
          />
        ) : (
          <AppBarBackTitle title={pageTitle} onBack={onBack} />
        )
      }
    >
      <List disablePadding>
        {userId && (
          <SettingsSection sx={{ marginBottom: '10px', padding: '5px 0' }}>
            <UserListItemWithStatus userId={userId} />
          </SettingsSection>
        )}

        <RoomPermissions
          subHeader={subTitle}
          rights={rights}
          initialPermissions={initialPermissions}
          ref={permissionsRef}
          setIsAllChecked={setIsAllChecked}
        />

        {onSecondaryActionButton &&
          secondaryActionButtonProps.text &&
          secondaryActionButtonProps.color && (
            <SettingsSection>
              <ListItemButton
                sx={{ height: '50px' }}
                onClick={onSecondaryActionButton}
              >
                <Typography
                  color={secondaryActionButtonProps.color}
                  variant="subtitle3"
                >
                  {secondaryActionButtonProps.text}
                </Typography>
              </ListItemButton>
            </SettingsSection>
          )}

        {actionButtonProps.color && actionButtonProps.text && (
          <SettingsSection>
            <ListItemButton
              sx={{ height: '50px' }}
              onClick={onActionButtonClick}
            >
              <Typography color={actionButtonProps.color} variant="subtitle3">
                {actionButtonProps.text}
              </Typography>
            </ListItemButton>
          </SettingsSection>
        )}
      </List>
    </PageWithAppBar>
  )
}
